// LLM generated - Model Claude Opus 4
import type { PublicPredictionMarket } from '../services/interfaces/market.types';
import type { PusherMarketUpdatePayload, PusherOrderbook } from '../services/interfaces/market.pusher.types';
import { marketLiveUpdatesManager } from '../services/marketLiveUpdates.service';
import { useNormalizedMarketsStore } from '../state/normalizedMarkets.store';

/**
 * Default market group ID for testing (Seahawks vs Patriots)
 */
const DEFAULT_MARKET_GROUP_ID = 'pmgm_697707ea4016e7dec38a082db25144d8';

/**
 * Sample market update data based on real Pusher payload
 */
const SAMPLE_MARKET_UPDATE = {
  id: 'pm_697ecfff055e74dce0ba1c9f2d5f3494',
  title: 'Seahawks at Patriots: Total Points',
  sub_category: 'nfl' as const,
  type: 'total' as const,
  event_id: 'seahawks-patriots-event',
  event_start: 1770168600000,
  line: 45.5,
  yes_line_display: 'O 45.5',
  no_line_display: 'U 45.5',
  yes_team_id: 'team-1',
  no_team_id: 'team-2',
  price_yes: {
    american: '-110',
    decimal: '1.91x',
    percentage: '51%',
    price: '$0.51',
    numeric_price: 0.51,
  },
  price_no: {
    american: '-110',
    decimal: '1.91x',
    percentage: '50%',
    price: '$0.50',
    numeric_price: 0.50,
  },
  status: 'active' as const,
  rules: 'Sample rules for testing',
  market_group_id: DEFAULT_MARKET_GROUP_ID,
  orderbook: {
    bids: { yes: [], no: [] },
    asks: { yes: [], no: [] },
  } as PusherOrderbook,
};

type MarketUpdateOverrides = Partial<typeof SAMPLE_MARKET_UPDATE> & {
  updated_at?: number;
};

/**
 * Tracks timestamp state for each market
 */
const marketTimestamps = new Map<string, number>();

/**
 * Active auto-update intervals
 */
const autoUpdateIntervals = new Map<string, ReturnType<typeof setInterval>>();

/**
 * Creates a fake Pusher market update payload
 */
function createMarketUpdate(
  overrides?: MarketUpdateOverrides
): PusherMarketUpdatePayload {
  const marketId = overrides?.id ?? SAMPLE_MARKET_UPDATE.id;

  // Get or initialize timestamp for this market
  let currentTimestamp = marketTimestamps.get(marketId) ?? Date.now();

  // If updated_at is provided, use it; otherwise increment current
  if (overrides?.updated_at !== undefined) {
    currentTimestamp = overrides.updated_at;
  } else {
    currentTimestamp += 1; // Increment by 1ms to ensure change
  }

  marketTimestamps.set(marketId, currentTimestamp);

  const message = {
    ...SAMPLE_MARKET_UPDATE,
    ...overrides,
    updated_at: currentTimestamp,
  };

  return {
    message: message as unknown as PusherMarketUpdatePayload['message'],
    metadata: {
      id: marketId,
    },
  };
}

/**
 * Simulates a single Pusher update for a market
 * @param marketId The market ID to update (uses sample ID if not provided)
 * @param overrides Optional overrides for the market data
 */
function simulateUpdate(
  marketId?: string,
  overrides?: MarketUpdateOverrides
): void {
  const payload = createMarketUpdate({
    ...overrides,
    id: marketId ?? SAMPLE_MARKET_UPDATE.id,
  });

  marketLiveUpdatesManager.simulateUpdate(payload);
}

/**
 * Simulates an update with the same timestamp (should be skipped)
 * Useful for testing that unchanged timestamps don't trigger re-renders
 */
function simulateUpdateWithSameTimestamp(marketId?: string): void {
  const id = marketId ?? SAMPLE_MARKET_UPDATE.id;
  const existingTimestamp = marketTimestamps.get(id);

  if (existingTimestamp === undefined) {
    // First update - create with initial timestamp
    simulateUpdate(id);
    return;
  }

  // Use the same timestamp - this should be skipped
  const payload = createMarketUpdate({
    id,
    updated_at: existingTimestamp,
  });

  marketLiveUpdatesManager.simulateUpdate(payload);
}

/**
 * Starts auto-updating a market at specified interval
 * @param marketId The market ID to update
 * @param intervalMs Update interval in milliseconds
 * @returns Cleanup function to stop auto-updates
 */
function startAutoUpdates(
  marketId?: string,
  intervalMs = 1000
): () => void {
  const id = marketId ?? SAMPLE_MARKET_UPDATE.id;

  // Stop any existing auto-updates for this market
  stopAutoUpdates(id);

  // Start new interval
  const intervalId = setInterval(() => {
    simulateUpdate(id);
  }, intervalMs);

  autoUpdateIntervals.set(id, intervalId);

  // Return cleanup function
  return () => stopAutoUpdates(id);
}

/**
 * Stops auto-updates for a specific market
 */
function stopAutoUpdates(marketId?: string): void {
  const id = marketId ?? SAMPLE_MARKET_UPDATE.id;
  const intervalId = autoUpdateIntervals.get(id);

  if (intervalId) {
    clearInterval(intervalId);
    autoUpdateIntervals.delete(id);
  }
}

/**
 * Stops all auto-updates
 */
function stopAllAutoUpdates(): void {
  for (const intervalId of autoUpdateIntervals.values()) {
    clearInterval(intervalId);
  }
  autoUpdateIntervals.clear();
}

/**
 * Resets all faker state (timestamps and auto-updates)
 */
function reset(): void {
  stopAllAutoUpdates();
  marketTimestamps.clear();
}

/**
 * Gets the current timestamp for a market
 */
function getMarketTimestamp(marketId?: string): number | undefined {
  return marketTimestamps.get(marketId ?? SAMPLE_MARKET_UPDATE.id);
}

/**
 * Gets all markets for a market group from the store
 */
function getMarketsForGroup(marketGroupId: string = DEFAULT_MARKET_GROUP_ID): PublicPredictionMarket[] {
  const state = useNormalizedMarketsStore.getState();
  const marketGroup = state.marketGroupsById.get(marketGroupId);
  return marketGroup?.markets ?? [];
}

/**
 * Gets all market IDs for a market group
 */
function getMarketIdsForGroup(marketGroupId: string = DEFAULT_MARKET_GROUP_ID): string[] {
  return getMarketsForGroup(marketGroupId).map(m => m.id);
}

/**
 * Market type options for filtering
 * - 'spread' = Win By column
 * - 'total' = Total column  
 * - 'moneyline' = Winner column
 */
type MarketTypeFilter = 'spread' | 'total' | 'moneyline' | 'all';

/**
 * Options for simulateGroupUpdate
 */
type SimulateGroupUpdateOptions = {
  /** Price change to apply (e.g., 0.05 for +5%) */
  priceChange?: number;
  /** Which market type to update: 'spread' (Win By), 'total', 'moneyline' (Winner), or 'all' */
  marketType?: MarketTypeFilter;
};

/**
 * Simulates updates for markets in a market group
 * @param marketGroupId The market group ID (defaults to Seahawks vs Patriots)
 * @param options Options including priceChange and marketType filter
 */
function simulateGroupUpdate(
  marketGroupId: string = DEFAULT_MARKET_GROUP_ID,
  options: SimulateGroupUpdateOptions | number = {}
): void {
  // Support legacy signature: simulateGroupUpdate(groupId, priceChange)
  const opts: SimulateGroupUpdateOptions = typeof options === 'number' 
    ? { priceChange: options } 
    : options;
  
  const { priceChange, marketType = 'all' } = opts;
  
  let markets = getMarketsForGroup(marketGroupId);
  
  if (markets.length === 0) {
    console.warn(`[pusherFaker] No markets found for group ${marketGroupId}`);
    return;
  }

  // Filter by market type if specified
  if (marketType !== 'all') {
    markets = markets.filter(m => m.type === marketType);
    if (markets.length === 0) {
      console.warn(`[pusherFaker] No '${marketType}' markets found in group ${marketGroupId}`);
      return;
    }
  }

  const typeLabel = marketType === 'all' ? 'all' : marketType;
  // eslint-disable-next-line no-console
  console.log(`[pusherFaker] Updating ${markets.length} ${typeLabel} market(s) in group ${marketGroupId}`);

  for (const market of markets) {
    const newTimestamp = Date.now();
    marketTimestamps.set(market.id, newTimestamp);

    // Apply price change if specified
    let updatedMarket = { ...market };
    if (priceChange !== undefined && 'price_yes' in market) {
      const currentPrice = market.price_yes.numeric_price ?? 0.5;
      const newPrice = Math.min(0.99, Math.max(0.01, currentPrice + priceChange));
      const newPercentage = Math.round(newPrice * 100);
      
      updatedMarket = {
        ...market,
        price_yes: {
          ...market.price_yes,
          numeric_price: newPrice,
          percentage: `${newPercentage}%`,
          price: `$${newPrice.toFixed(2)}`,
        },
        price_no: {
          ...market.price_no,
          numeric_price: 1 - newPrice,
          percentage: `${100 - newPercentage}%`,
          price: `$${(1 - newPrice).toFixed(2)}`,
        },
      };
    }

    const payload: PusherMarketUpdatePayload = {
      message: {
        ...updatedMarket,
        updated_at: newTimestamp,
      },
      metadata: {
        id: market.id,
      },
    };

    marketLiveUpdatesManager.simulateUpdate(payload);
  }
}

/**
 * Shorthand to update only the spread (Win By) market
 */
function updateSpread(marketGroupId?: string, priceChange?: number): void {
  simulateGroupUpdate(marketGroupId, { priceChange, marketType: 'spread' });
}

/**
 * Shorthand to update only the total market
 */
function updateTotal(marketGroupId?: string, priceChange?: number): void {
  simulateGroupUpdate(marketGroupId, { priceChange, marketType: 'total' });
}

/**
 * Shorthand to update only the moneyline (Winner) markets
 */
function updateMoneyline(marketGroupId?: string, priceChange?: number): void {
  simulateGroupUpdate(marketGroupId, { priceChange, marketType: 'moneyline' });
}

/**
 * Starts auto-updating markets in a group
 */
function startGroupAutoUpdates(
  marketGroupId: string = DEFAULT_MARKET_GROUP_ID,
  intervalMs = 2000,
  options: SimulateGroupUpdateOptions = {}
): () => void {
  const intervalId = setInterval(() => {
    simulateGroupUpdate(marketGroupId, options);
  }, intervalMs);

  autoUpdateIntervals.set(`group:${marketGroupId}`, intervalId);

  return () => {
    clearInterval(intervalId);
    autoUpdateIntervals.delete(`group:${marketGroupId}`);
  };
}

/**
 * Lists all market groups in the store (for debugging)
 */
function listMarketGroups(): { id: string; markets: number }[] {
  const state = useNormalizedMarketsStore.getState();
  return Array.from(state.marketGroupsById.entries()).map(([id, group]) => ({
    id,
    markets: group.markets.length,
  }));
}

/**
 * Pusher faker utility for testing market updates with simulated timestamps.
 *
 * Usage in browser console:
 * ```javascript
 * // List all market groups in the store
 * window.pusherFaker.listMarketGroups()
 *
 * // Update all markets in a group with price change
 * window.pusherFaker.simulateGroupUpdate('pmgm_xxx', { priceChange: 0.05 })
 *
 * // Update only specific market types:
 * window.pusherFaker.updateSpread('pmgm_xxx', 0.05)   // Win By column
 * window.pusherFaker.updateTotal('pmgm_xxx', 0.05)    // Total column
 * window.pusherFaker.updateMoneyline('pmgm_xxx', 0.05) // Winner column
 *
 * // Or use options object:
 * window.pusherFaker.simulateGroupUpdate('pmgm_xxx', { 
 *   priceChange: 0.05, 
 *   marketType: 'spread'  // 'spread' | 'total' | 'moneyline' | 'all'
 * })
 *
 * // Start auto-updates for specific market type
 * const stop = window.pusherFaker.startGroupAutoUpdates('pmgm_xxx', 2000, { 
 *   priceChange: 0.01, 
 *   marketType: 'total' 
 * })
 * // Later: stop()
 * ```
 */
export const pusherFaker = {
  // Single market updates
  createMarketUpdate,
  simulateUpdate,
  simulateUpdateWithSameTimestamp,
  startAutoUpdates,
  stopAutoUpdates,
  stopAllAutoUpdates,
  getMarketTimestamp,
  
  // Group updates (for real markets from store)
  getMarketsForGroup,
  getMarketIdsForGroup,
  simulateGroupUpdate,
  startGroupAutoUpdates,
  listMarketGroups,
  
  // Shorthand methods for specific market types
  updateSpread,    // Win By column
  updateTotal,     // Total column
  updateMoneyline, // Winner column
  
  // Utilities
  reset,
  SAMPLE_MARKET_UPDATE,
  DEFAULT_MARKET_GROUP_ID,
};

// Expose on window for console testing in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as unknown as { pusherFaker: typeof pusherFaker }).pusherFaker = pusherFaker;
}
